/**
 * Content-side spam signal checks.
 *
 * Deliberately not a SpamAssassin clone. The score is a diagnostic for cold
 * outreach: it flags the things that actually move placement for a
 * one-to-one-looking business email (link count, image-only bodies, missing
 * unsubscribe, shouty subjects) rather than trying to reproduce a filter's
 * proprietary weighting, which nobody can do honestly from outside.
 *
 * Pure function, no I/O. In @aegis/shared so the public tool can score a
 * draft entirely in the browser — the message body never reaches us.
 */

export type IssueSeverity = 'critical' | 'warning' | 'info';

export interface ContentIssue {
  severity: IssueSeverity;
  code: string;
  message: string;
  evidence?: string;
}

export interface ContentCheckResult {
  /** 0-100, higher is safer. */
  score: number;
  issues: ContentIssue[];
  stats: {
    subject_length: number;
    word_count: number;
    link_count: number;
    image_count: number;
    caps_ratio: number;
    exclamation_count: number;
    spam_phrase_count: number;
  };
}

/**
 * Phrases with a well-documented association with filtered mail. Weighted:
 * 'critical' phrases are near-automatic filtering in a cold context.
 */
const SPAM_PHRASES: { phrase: string; weight: number }[] = [
  { phrase: 'act now', weight: 3 },
  { phrase: 'apply now', weight: 2 },
  { phrase: 'buy direct', weight: 3 },
  { phrase: 'call now', weight: 3 },
  { phrase: 'cash bonus', weight: 4 },
  { phrase: 'cheap', weight: 2 },
  { phrase: 'click here', weight: 3 },
  { phrase: 'click below', weight: 2 },
  { phrase: 'credit card', weight: 2 },
  { phrase: 'dear friend', weight: 4 },
  { phrase: 'double your', weight: 4 },
  { phrase: 'earn extra cash', weight: 4 },
  { phrase: 'eliminate debt', weight: 4 },
  { phrase: 'financial freedom', weight: 3 },
  { phrase: 'free access', weight: 2 },
  { phrase: 'free gift', weight: 3 },
  { phrase: 'free money', weight: 4 },
  { phrase: 'free trial', weight: 1 },
  { phrase: 'guarantee', weight: 2 },
  { phrase: 'increase sales', weight: 2 },
  { phrase: 'limited time', weight: 2 },
  { phrase: 'lowest price', weight: 3 },
  { phrase: 'make money', weight: 4 },
  { phrase: 'no credit check', weight: 4 },
  { phrase: 'no obligation', weight: 3 },
  { phrase: 'no strings attached', weight: 3 },
  { phrase: 'once in a lifetime', weight: 3 },
  { phrase: 'order now', weight: 3 },
  { phrase: 'risk free', weight: 3 },
  { phrase: 'satisfaction guaranteed', weight: 3 },
  { phrase: 'special promotion', weight: 2 },
  { phrase: 'this is not spam', weight: 5 },
  { phrase: 'urgent', weight: 2 },
  { phrase: 'winner', weight: 3 },
  { phrase: 'you have been selected', weight: 4 },
  { phrase: '100% free', weight: 4 },
  { phrase: '100% satisfied', weight: 3 },
];

/** URL shorteners hide the destination, which filters treat as evasion. */
const SHORTENER_DOMAINS = [
  'bit.ly', 'tinyurl.com', 't.co', 'goo.gl', 'ow.ly', 'is.gd', 'buff.ly',
  'rebrand.ly', 'cutt.ly', 'shorturl.at', 'rb.gy', 'tiny.cc', 'lnkd.in',
];

export interface ContentCheckInput {
  subject: string;
  body: string;
  /** Whether `body` is HTML. Plain-text bodies skip the image/HTML checks. */
  html?: boolean;
}

function stripHtml(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function checkEmailContent(input: ContentCheckInput): ContentCheckResult {
  const { subject = '', body = '', html = false } = input;
  const text = html ? stripHtml(body) : body;
  const haystack = `${subject}\n${text}`.toLowerCase();

  const issues: ContentIssue[] = [];
  let score = 100;
  const penalize = (points: number, issue: ContentIssue) => {
    score -= points;
    issues.push(issue);
  };

  // ─── Links ──────────────────────────────────────────────────────────────
  const urls = [
    ...(html ? [...body.matchAll(/href\s*=\s*["']([^"']+)["']/gi)].map((m) => m[1]) : []),
    ...[...text.matchAll(/https?:\/\/[^\s<>")\]]+/gi)].map((m) => m[0]),
  ];
  const uniqueUrls = [...new Set(urls.filter((u) => /^https?:/i.test(u)))];

  if (uniqueUrls.length > 5) {
    penalize(12, {
      severity: 'warning',
      code: 'too_many_links',
      message: `${uniqueUrls.length} links. Cold email with more than 2-3 links reads as bulk mail.`,
    });
  } else if (uniqueUrls.length > 2) {
    penalize(4, {
      severity: 'info',
      code: 'several_links',
      message: `${uniqueUrls.length} links. One clear call to action performs better and looks less like bulk.`,
    });
  }

  const shorteners = uniqueUrls.filter((u) =>
    SHORTENER_DOMAINS.some((d) => u.toLowerCase().includes(d)),
  );
  if (shorteners.length > 0) {
    penalize(15, {
      severity: 'critical',
      code: 'url_shortener',
      message: 'URL shorteners hide the destination and are treated as evasion. Use your own tracking domain.',
      evidence: shorteners.join(', '),
    });
  }

  // ─── Images and text balance ────────────────────────────────────────────
  const imageCount = html ? (body.match(/<img\b/gi) ?? []).length : 0;
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  if (html && imageCount > 0 && wordCount < 30) {
    penalize(20, {
      severity: 'critical',
      code: 'image_heavy',
      message: 'Almost all image, very little text. Image-only mail is a classic filter trigger.',
    });
  }

  if (wordCount === 0) {
    penalize(30, { severity: 'critical', code: 'empty_body', message: 'The body is empty.' });
  } else if (wordCount < 15) {
    penalize(5, {
      severity: 'info',
      code: 'very_short',
      message: 'Very short body. Extremely short mail with a link is a common spam shape.',
    });
  } else if (wordCount > 350) {
    penalize(5, {
      severity: 'info',
      code: 'very_long',
      message: `${wordCount} words. Long cold emails get skimmed and deleted, which hurts engagement signals.`,
    });
  }

  // ─── Subject line ───────────────────────────────────────────────────────
  if (subject.trim().length === 0) {
    penalize(25, { severity: 'critical', code: 'no_subject', message: 'No subject line.' });
  } else if (subject.length > 70) {
    penalize(4, {
      severity: 'info',
      code: 'long_subject',
      message: `Subject is ${subject.length} characters and will be truncated on mobile.`,
    });
  }

  const subjectLetters = subject.replace(/[^A-Za-z]/g, '');
  const capsRatio = subjectLetters.length >= 6
    ? subjectLetters.replace(/[^A-Z]/g, '').length / subjectLetters.length
    : 0;
  if (capsRatio > 0.6) {
    penalize(15, {
      severity: 'critical',
      code: 'shouting_subject',
      message: 'Subject is mostly capital letters.',
      evidence: subject,
    });
  }

  const exclamations = (`${subject} ${text}`.match(/!/g) ?? []).length;
  if (exclamations > 3) {
    penalize(8, {
      severity: 'warning',
      code: 'excessive_exclamation',
      message: `${exclamations} exclamation marks.`,
    });
  }

  // ─── Spam vocabulary ────────────────────────────────────────────────────
  const matched = SPAM_PHRASES.filter((p) => haystack.includes(p.phrase));
  const phraseWeight = matched.reduce((sum, p) => sum + p.weight, 0);
  if (phraseWeight > 0) {
    penalize(Math.min(30, phraseWeight * 2), {
      severity: phraseWeight >= 8 ? 'critical' : phraseWeight >= 4 ? 'warning' : 'info',
      code: 'spam_vocabulary',
      message: `${matched.length} spam-associated phrase(s) detected.`,
      evidence: matched.map((p) => p.phrase).join(', '),
    });
  }

  // ─── Compliance ─────────────────────────────────────────────────────────
  if (!/unsubscribe|opt.?out|stop receiving|no longer wish/i.test(text)) {
    penalize(10, {
      severity: 'warning',
      code: 'no_unsubscribe',
      message: 'No visible unsubscribe wording. Required for bulk sending and a strong trust signal.',
    });
  }

  // ─── Evasion tricks ─────────────────────────────────────────────────────
  // Cyrillic/Greek lookalikes inside otherwise-Latin text are a deliberate
  // filter-evasion technique, and filters penalize it heavily.
  const homoglyphs = text.match(/[Ѐ-ӿͰ-Ͽ]/g);
  if (homoglyphs && /[A-Za-z]/.test(text)) {
    penalize(20, {
      severity: 'critical',
      code: 'homoglyphs',
      message: 'Non-Latin lookalike characters mixed into Latin text — filters read this as deliberate evasion.',
      evidence: [...new Set(homoglyphs)].join(' '),
    });
  }

  if (html && /<[^>]+style\s*=\s*["'][^"']*(display\s*:\s*none|font-size\s*:\s*0)/i.test(body)) {
    penalize(20, {
      severity: 'critical',
      code: 'hidden_text',
      message: 'Hidden text detected (display:none or zero font size).',
    });
  }

  if (html && !/<\/p>|<br|<div/i.test(body) && body.length > 400) {
    penalize(3, {
      severity: 'info',
      code: 'no_structure',
      message: 'Long HTML body with no paragraph structure.',
    });
  }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    issues,
    stats: {
      subject_length: subject.length,
      word_count: wordCount,
      link_count: uniqueUrls.length,
      image_count: imageCount,
      caps_ratio: Number(capsRatio.toFixed(2)),
      exclamation_count: exclamations,
      spam_phrase_count: matched.length,
    },
  };
}
